/**
 * How long an analysis should take, stage by stage (Dax, 2026-10-06): broad, so a person knows whether to
 * wait a minute or come back in two hours. The rates are wall seconds on the one worker, measured from the
 * worker's own stage log across 165 analyses and a 28-minute, four-part 4K project, set between the quiet and
 * the busy box. Decoding scales with the source's pixels times its length (megapixel-seconds); the steps that
 * read the 720p proxy or its sound scale with length alone; the rest are near constant.
 */
/**
 * Past this a clip's analysis waits for a person's go-ahead instead of starting on its own (Dax, 2026-10-06),
 * and gets one try: a 28-minute project took hours and failed at its last step more than once, each retry
 * spending hours again. A failed long run keeps whatever it built (the preview, poster, waveform, frames, any
 * transcript), so the clip can still be opened, cut by hand, tagged and used.
 */
export const LONG_CLIP_MS = 10 * 60 * 1000;
export const ANALYSE_STAGES = ['filing', 'probing', 'converting', 'partFrames', 'previews', 'frames', 'transcribing', 'sounds', 'scenes', 'watching', 'placing', 'tagging'];
export const ANALYSE_RATES = {
    filingS: 2,
    probingBaseS: 5,
    probingPerMpxS: 0.01,
    /** Converting a part down to the cap, 10-bit 4K HEVC to 1080p H.264: 126 s for 60 s of iPhone 4K (2026-10-06). */
    convertingPerMpxS: 0.25,
    /** Every frame of every part, read for a project's joins (several parts only). */
    partFramesPerMpxS: 0.4,
    /** The 720p proxy, decoded from the raw parts and encoded. */
    previewsPerMpxS: 0.3,
    previewsPerS: 0.3,
    framesBaseS: 5,
    framesPerS: 0.06,
    transcribingBaseS: 10,
    transcribingPerS: 0.27,
    soundsS: 10,
    scenesS: 2,
    watchingBaseS: 30,
    watchingPerS: 0.05,
    placingS: 15,
    taggingS: 5,
};
/**
 * Whether a run transcribes a clip's speech: the one rule (Dax, 2026-10-06). Asked for explicitly, always;
 * by default, every clip up to LONG_CLIP_MS, and a longer one only where the brand has switched that on.
 */
export function transcribesSpeech(voice, durationMs, transcribeLong) {
    if (voice === 'none')
        return false;
    return voice === 'always' || durationMs <= LONG_CLIP_MS || transcribeLong;
}
/** Seconds transcribing a clip's speech takes, alone or inside an analysis. */
export function transcribeSeconds(durationMs) {
    return ANALYSE_RATES.transcribingBaseS + ANALYSE_RATES.transcribingPerS * (durationMs / 1000);
}
/** Each stage the analysis will run, with its expected seconds; stages it skips are absent. */
export function analyseStageSeconds(w) {
    const r = ANALYSE_RATES;
    const s = w.durationMs / 1000;
    return {
        filing: r.filingS,
        probing: r.probingBaseS + r.probingPerMpxS * (w.sourceMpxS + w.convertMpxS),
        ...(w.convertMpxS > 0 ? { converting: r.convertingPerMpxS * w.convertMpxS } : {}),
        ...(w.parts > 1 ? { partFrames: r.partFramesPerMpxS * w.sourceMpxS } : {}),
        previews: r.previewsPerMpxS * w.sourceMpxS + r.previewsPerS * s,
        frames: r.framesBaseS + r.framesPerS * s,
        ...(w.hasAudio && w.transcribe ? { transcribing: transcribeSeconds(w.durationMs) } : {}),
        ...(w.hasAudio ? { sounds: r.soundsS } : {}),
        scenes: r.scenesS,
        ...(w.ai ? { watching: r.watchingBaseS + r.watchingPerS * s, placing: r.placingS } : {}),
        tagging: r.taggingS,
    };
}
/** Milliseconds still to run from the start of `from`, with `doneShare` (0 to 1) of that stage already behind it. */
export function analyseRemainingMs(w, from, doneShare = 0) {
    const secs = analyseStageSeconds(w);
    const at = ANALYSE_STAGES.indexOf(from);
    let total = 0;
    for (const [i, key] of ANALYSE_STAGES.entries()) {
        const stage = secs[key];
        if (stage === undefined || i < at)
            continue;
        total += i === at ? stage * (1 - Math.min(1, Math.max(0, doneShare))) : stage;
    }
    return Math.round(total * 1000);
}
const MIN = 60_000;
const HOUR = 60 * MIN;
/** A stretch of analysis in broad words: hours past ninety minutes, then an hour, half an hour, a quarter, minutes. */
export function analyseDurationWords(ms) {
    if (ms > 90 * MIN)
        return `about ${Math.round(ms / HOUR)} hrs`;
    if (ms > 45 * MIN)
        return 'about 1 hr';
    if (ms > 20 * MIN)
        return 'about 30 min';
    if (ms > 10 * MIN)
        return 'about 15 min';
    if (ms > 2 * MIN)
        return `about ${Math.ceil(ms / (5 * MIN)) * 5} min`;
    return 'a minute or two';
}
/**
 * The time left in words, broad on purpose. Past the expected end it says so rather than counting below zero.
 * Null when nothing is expected.
 */
export function analyseTimeLeft(etaAt, now) {
    if (!etaAt)
        return null;
    const left = new Date(etaAt).getTime() - now;
    if (!Number.isFinite(left))
        return null;
    if (left <= 0)
        return 'taking longer than expected';
    if (left <= 2 * MIN)
        return 'almost done';
    return `${analyseDurationWords(left)} left`;
}
//# sourceMappingURL=analyse-estimate.js.map